import { Navigate, useLocation } from 'react-router-dom';
import { resolveUserRole } from '../../utils/roleUtils';
import { getValidAuthToken, hydrateAuthFromSearchParams } from '../../utils/api/auth';
import {
  canAccess,
  getFirstAccessibleAdminRoute,
  resolveAdminPermissionFallback,
} from '../../utils/permissionUtils';
import { LOGIN_URL } from '../../utils/api/config';

const PLATFORM_STAFF_ROLES = ['super_admin', 'manager', 'staff'];
const COMPANY_ROLES = ['company', 'company_staff'];

const isPlatformAdminRole = (role) => PLATFORM_STAFF_ROLES.includes(role);
const isCompanyRole = (role) => COMPANY_ROLES.includes(role);

/**
 * Protected Route Component
 * Handles role-based and permission-based route protection
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {string[]} props.allowedRoles - Array of allowed roles (e.g., ['super_admin', 'manager', 'staff'])
 * @param {string|string[]} props.requiredPermissions - Required permission(s) (e.g., 'companies.view' or ['companies.view', 'companies.create'])
 * @param {boolean} props.requireAuth - Whether authentication is required (default: true)
 * @param {string} props.redirectTo - Path to redirect if unauthorized (default: '/')
 */
const ProtectedRoute = ({
  children,
  allowedRoles = [],
  requiredPermissions = null,
  requireAuth = true,
  redirectTo = '/'
}) => {
  const location = useLocation();
  const tokenFromUrl = new URLSearchParams(location.search).get('token');
  const hasIncomingAuthHandoff = Boolean(tokenFromUrl);

  // Hydrate localStorage synchronously so role checks work on the first render after login redirect
  hydrateAuthFromSearchParams(location.search);

  const token = getValidAuthToken();
  const userRole = resolveUserRole(location.search);
  const isAdmin = isPlatformAdminRole(userRole);
  const isCompany = isCompanyRole(userRole);
  const isSuperAdmin = userRole === 'super_admin';

  if (requireAuth && !token && !hasIncomingAuthHandoff) {
    window.location.href = LOGIN_URL;
    return null;
  }

  if (allowedRoles.length === 0) {
    return children;
  }

  const permissionFallback = resolveAdminPermissionFallback(location.pathname);

  if (userRole && allowedRoles.includes(userRole)) {
    if (requiredPermissions && !canAccess(requiredPermissions) && !isSuperAdmin) {
      return (
        <Navigate
          to={permissionFallback}
          replace
          state={{ from: location, permissionDenied: true }}
        />
      );
    }
    return children;
  }

  if (isAdmin) {
    if (requiredPermissions && !canAccess(requiredPermissions) && !isSuperAdmin) {
      return (
        <Navigate
          to={permissionFallback}
          replace
          state={{ from: location, permissionDenied: true }}
        />
      );
    }

    if (location.pathname.startsWith('/admin/')) {
      return (
        <Navigate
          to={getFirstAccessibleAdminRoute()}
          replace
          state={{ from: location, roleDenied: true }}
        />
      );
    }

    return (
      <Navigate
        to={getFirstAccessibleAdminRoute()}
        replace
        state={{ from: location }}
      />
    );
  }

  if (isCompany) {
    if (location.pathname === '/' || location.pathname.startsWith('/company')) {
      return children;
    }
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  if (hasIncomingAuthHandoff && !userRole) {
    return null;
  }

  if (!userRole && requireAuth) {
    window.location.href = LOGIN_URL;
    return null;
  }

  return <Navigate to={redirectTo} replace state={{ from: location }} />;
};

export default ProtectedRoute;
